/**
 * React Query에서 사용할 query key를 정의합니다.
 * 관련 데이터 타입별로 객체로 구분하여 관리합니다.
 */
export const QueryKeys = {
  // 사용자 관련 쿼리 키
  user: {
    all: ["users"] as const,
    list: (params?: any) =>
      params
        ? [...QueryKeys.user.all, params]
        : ([...QueryKeys.user.all] as const),
    detail: (id: string) => [...QueryKeys.user.all, id] as const,
    me: () => [...QueryKeys.user.all, "me"] as const,
  },
  // 그룹 관련 쿼리 키
  group: {
    all: ["groups"] as const,
    list: (params?: any) =>
      params
        ? [...QueryKeys.group.all, params]
        : ([...QueryKeys.group.all] as const),
    detail: (id: string) => [...QueryKeys.group.all, id] as const,
  },
  // 시험 관련 쿼리 키
  exam: {
    all: ["exams"] as const,
    list: (params?: any) =>
      params
        ? [...QueryKeys.exam.all, params]
        : ([...QueryKeys.exam.all] as const),
    detail: (id: string) => [...QueryKeys.exam.all, id] as const,
  },
  // 문제 관련 쿼리 키
  question: {
    all: ["questions"] as const,
    list: (params?: any) =>
      params
        ? [...QueryKeys.question.all, params]
        : ([...QueryKeys.question.all] as const),
    detail: (id: string) => [...QueryKeys.question.all, id] as const,
    byExam: (examId: string) =>
      [...QueryKeys.question.all, "exam", examId] as const,
    highlights: (examProgressId: string, questionId: string) =>
      [
        ...QueryKeys.question.all,
        "highlights",
        examProgressId,
        questionId,
      ] as const,
  },
  // 할당된 시험 관련 쿼리 키
  allocatedExam: {
    all: ["allocated_exams"] as const,
    list: (params?: any) =>
      params
        ? [...QueryKeys.allocatedExam.all, params]
        : ([...QueryKeys.allocatedExam.all] as const),
    detail: (id: string) => [...QueryKeys.allocatedExam.all, id] as const,
    byUser: (userId: string) =>
      [...QueryKeys.allocatedExam.all, "user", userId] as const,
    byExam: (examId: string) =>
      [...QueryKeys.allocatedExam.all, "exam", examId] as const,
  },
  // 시험 할당 관련 쿼리 키
  examAssignment: {
    all: ["exam_assignments"] as const,
    list: (params?: any) =>
      params
        ? [...QueryKeys.examAssignment.all, params]
        : ([...QueryKeys.examAssignment.all] as const),
    detail: (id: string) => [...QueryKeys.examAssignment.all, id] as const,
    byExam: (examId: string) =>
      [...QueryKeys.examAssignment.all, "exam", examId] as const,
    byAssigner: (assignerId: string) =>
      [...QueryKeys.examAssignment.all, "assigner", assignerId] as const,
    monitor: (id: string) =>
      [...QueryKeys.examAssignment.all, "monitor", id] as const,
  },
  // 카테고리 관련 쿼리 키
  category: {
    all: ["categories"] as const,
    list: (params?: any) =>
      params
        ? [...QueryKeys.category.all, params]
        : ([...QueryKeys.category.all] as const),
    detail: (id: string) => [...QueryKeys.category.all, id] as const,
    byGroup: (group: string) =>
      [...QueryKeys.category.all, "group", group] as const,
  },
  // 시험지 관련 쿼리 키
  examPaper: {
    all: ["exam_papers"] as const,
    list: (params?: any) =>
      params
        ? [...QueryKeys.examPaper.all, params]
        : ([...QueryKeys.examPaper.all] as const),
    detail: (id: string) => [...QueryKeys.examPaper.all, id] as const,
    questionsEditable: (id: string) =>
      [...QueryKeys.examPaper.all, id, "questions_editable"] as const,
  },
  examReport: {
    all: ["exam_reports"] as const,
    detail: (id: string) => [...QueryKeys.examReport.all, id] as const,
  },
  examProgress: {
    all: ["exam_progress"] as const,
    list: (params?: any) => [...QueryKeys.examProgress.all, params] as const,
    detail: (id: string) => [...QueryKeys.examProgress.all, id] as const,
    mine: (params?: any) =>
      [...QueryKeys.examProgress.all, "mine", params] as const,
    getReport: (id: string) =>
      [...QueryKeys.examProgress.all, "report", id] as const,
    getRatio: (id: string) =>
      [...QueryKeys.examProgress.all, "ratio", id] as const,
  },
  // Score Conversion Table 관련 쿼리 키
  scoreConversionTable: {
    all: ["score_conversion_tables"] as const,
    list: (params?: any) =>
      params
        ? [...QueryKeys.scoreConversionTable.all, params]
        : ([...QueryKeys.scoreConversionTable.all] as const),
    detail: (id: string) => [...QueryKeys.scoreConversionTable.all, id] as const,
  },
  // 아래에 추가 쿼리 키 정의 (예: posts, comments 등)
} as const;
